import { useEffect, useState, type ReactNode } from "react";
import { AppShell, SidebarNav, type SidebarNavSection } from "@legends/ui";
import { logout, type AssignedEvent, type AuthUser, type AuthzMe, type Cave } from "./api";
import { LegendsBrand, ShellTitle, SidebarUser, monoLabelStyle } from "./sections";
// The operator cave console adopts the admin design language + IA (the scoped
// `.admin-scope` token layer), so the legend/director back-office matches the admin
// portal (the shared user directive; ADR 0164 extended to operators). The live
// broadcast consoles mounted inside (Director/Legend Go-Live) keep their RelayStudio
// structure (ADR 0087) — only their studioChrome tokens are harmonized to match.
import "./admin/adminTheme.css";
import {
  CaveEventsTab,
  CaveRollTab,
  CaveSecurityTab,
  CaveTicketsTab,
} from "./caveTabs";
import { CaveRecordingsTab } from "./RecordingsTab";
import CaveLobbyEditor from "./CaveLobbyEditor";
import DirectorConsole from "./DirectorConsole";
import { assignedToCaveEvent } from "./AssignedEventsScreen";

// Cave console (ADR 0008): non-super-admins with configurable caves land here.
// Pick a cave, then operate its Security · Events · Tickets surfaces. Each tab
// is the cave-scoped mirror of the global admin consoles.

type ConsoleTab = "security" | "events" | "tickets" | "lobby" | "recordings" | "rolls";

const TAB_LABEL: Record<ConsoleTab, string> = {
  security: "Security",
  events: "Events",
  tickets: "Tickets",
  lobby: "Cave Designer",
  recordings: "Recordings",
  rolls: "Pre/Post/Hold-roll",
};

export interface CaveConsoleScreenProps {
  caves: Cave[];
  user: AuthUser;
  authz: AuthzMe;
  onLogout: () => void;
  /** Impersonation strip, rendered in the shell's banner slot (see App.tsx). */
  banner?: ReactNode;
  /** Event-scoped production grants (ADR 0065) — an operator who ALSO configures
   *  caves still sees their per-event assignments here (surfaced near the console),
   *  opening the same per-event DirectorConsole without regressing the cave path. */
  assignedEvents?: AssignedEvent[];
}

// Cave console (ADR 0008/0009): a full-height AppShell whose left menu is the
// cave picker plus the Security · Events · Tickets sections for the selected
// cave. The selected tab renders full-width in the content region.
export default function CaveConsoleScreen({
  caves,
  user,
  authz,
  onLogout,
  banner,
  assignedEvents,
}: CaveConsoleScreenProps) {
  const [caveId, setCaveId] = useState<string>(caves[0]?.id ?? "");
  const [tab, setTab] = useState<ConsoleTab>("security");
  // An event-scoped assignment being directed (ADR 0065). When set, the content
  // region mounts that event's DirectorConsole in place of the cave tabs; picking
  // a cave or a CONFIGURE tab clears it.
  const [assignedId, setAssignedId] = useState<string | null>(null);
  const assigned = (assignedEvents ?? []).find((e) => e.eventId === assignedId) ?? null;

  // Adopt the admin language for the whole operator console (matching HomeScreen):
  // the body class re-skins every @legends/ui component + portaled dialog, and sets
  // `--ui-zoom: 1` so the console renders at the admin's 1x density. The nested
  // studioChrome consoles read that same `--ui-zoom` for their full-screen height,
  // so this keeps the zoom coupling consistent.
  useEffect(() => {
    document.body.classList.add("admin-scope");
    return () => document.body.classList.remove("admin-scope");
  }, []);

  const cave = caves.find((c) => c.id === caveId) ?? caves[0];
  // The Pre/Post-roll tab exists only when the super-admin enabled contributions
  // for this cave (ADR 0052); leaving it selected on a cave without the grant
  // falls back to Security so there's never an empty tab.
  const showRoll = !!cave?.allowRollContrib;
  useEffect(() => {
    if (tab === "rolls" && !showRoll) setTab("security");
  }, [tab, showRoll]);
  const roles = cave
    ? (authz.tenants.find((tenant) => tenant.id === cave.tenant)?.roles ?? [])
    : [];
  const isLegend = roles.includes("legend");
  const isDirector = roles.includes("director");
  const isAdmin = authz.superAdmin || roles.includes("admin");
  // A configurable-Cave user with neither production persona retains the
  // existing operator presentation. This is presentation only; cave_config
  // remains the backend authorization boundary for every action.
  const isOperator = !isLegend && !isDirector;
  const showGoLive = isAdmin || isOperator || isLegend;
  const showDirect = isAdmin || isOperator || isDirector;
  // Designer (storefront authoring) is admin-only (LSC-349); a non-admin never
  // sits on a now-hidden Designer tab.
  useEffect(() => {
    if (tab === "lobby" && !isAdmin) setTab("security");
  }, [tab, isAdmin]);

  const signOut = () => {
    logout().finally(onLogout);
  };

  const pickTab = (t: ConsoleTab) => {
    setAssignedId(null);
    setTab(t);
  };

  const sections: SidebarNavSection[] = [
    {
      title: "CAVES",
      items: caves.map((c) => ({
        label: c.name,
        active: c.id === cave?.id && !assigned,
        onClick: () => {
          setAssignedId(null);
          setCaveId(c.id);
        },
      })),
    },
    {
      title: "CONFIGURE",
      items: [
        {
          label: "Security",
          icon: "⊟",
          active: tab === "security" && !assigned,
          onClick: () => pickTab("security"),
        },
        {
          label: "Events",
          icon: "▤",
          active: tab === "events" && !assigned,
          onClick: () => pickTab("events"),
        },
        {
          label: "Tickets",
          icon: "◫",
          active: tab === "tickets" && !assigned,
          onClick: () => pickTab("tickets"),
        },
        // Designer (Cave storefront authoring) is an ADMIN capability, not a
        // Director/Legend one (LSC-349): production control and storefront authoring
        // are separate responsibilities, so a director-only operator never sees it.
        ...(isAdmin
          ? [
              {
                label: "Cave Designer",
                icon: "▦",
                active: tab === "lobby" && !assigned,
                onClick: () => pickTab("lobby"),
              },
            ]
          : []),
        {
          label: "Recordings",
          icon: "◉",
          active: tab === "recordings" && !assigned,
          onClick: () => pickTab("recordings"),
        },
        ...(showRoll
          ? [
              {
                label: "Pre/Post/Hold-roll",
                icon: "⧉",
                active: tab === "rolls" && !assigned,
                onClick: () => pickTab("rolls"),
              },
            ]
          : []),
      ],
    },
    // Event-scoped assignments (ADR 0065): shown only when the caller holds any,
    // so a plain cave operator's console is unchanged.
    ...(assignedEvents && assignedEvents.length > 0
      ? [
          {
            title: "ASSIGNED CAVECASTS",
            items: assignedEvents.map((e) => ({
              label: e.title,
              icon: "◈",
              active: assignedId === e.eventId,
              onClick: () => setAssignedId(e.eventId),
            })),
          },
        ]
      : []),
  ];

  return (
    <AppShell
      brand={<LegendsBrand />}
      title={
        <ShellTitle>
          {assigned
            ? `${assigned.caveName} · ${assigned.title}`
            : cave
              ? `${cave.name} · ${TAB_LABEL[tab]}`
              : "Cave console"}
        </ShellTitle>
      }
      sidebar={<SidebarNav sections={sections} />}
      sidebarFooter={
        <SidebarUser
          name={user.displayName || user.username}
          onSignOut={signOut}
        />
      }
      banner={banner}
    >
      {assigned ? (
        <div style={{ maxWidth: 900, display: "grid", gap: 12 }}>
          <p style={monoLabelStyle}>Event assignment · {assigned.caveName}</p>
          <DirectorConsole
            key={assigned.eventId}
            caveId={assigned.caveId}
            event={assignedToCaveEvent(assigned)}
            onClose={() => setAssignedId(null)}
          />
        </div>
      ) : (
        // Card-grid tabs (Events / Recordings / Pre-post-roll) go FULL WIDTH so the
        // responsive card grid uses the whole screen (more columns on a wider
        // monitor — the grid itself bounds each card's width). The form tabs stay
        // narrow for readability.
        <div style={{ maxWidth: tab === "events" || tab === "recordings" || tab === "rolls" ? undefined : 760 }}>
          {cave && tab === "security" && <CaveSecurityTab caveId={cave.id} />}
          {cave && tab === "events" && (
            <CaveEventsTab
              caveId={cave.id}
              showGoLive={showGoLive}
              showDirect={showDirect}
              canWrite={authz.catalogWrite}
            />
          )}
          {cave && tab === "tickets" && (
            <CaveTicketsTab caveId={cave.id} canWrite={authz.catalogWrite} />
          )}
          {cave && tab === "lobby" && isAdmin && (
            <CaveLobbyEditor key={cave.id} caveId={cave.id} caveName={cave.name} canPublish={isAdmin} />
          )}
          {cave && tab === "recordings" && <CaveRecordingsTab caveId={cave.id} recordDefault={cave.recordDefault} />}
          {cave && tab === "rolls" && showRoll && <CaveRollTab caveId={cave.id} />}
        </div>
      )}
    </AppShell>
  );
}
